import { useMemo, useState } from 'react';
import { Log } from '@/shared/Log';
import { queryClient } from '@/shared/Providers';
import { ConsultationTypeEnum } from '@/shared/enums';
import { useQuery } from '@tanstack/react-query';
import { fetchStopWords } from '@/features/booking/widgets/describe-problem/api/fetchStopwords';

export enum InputType {
    Subject,
    Message,
}

type StopWordDetails = {
    Words: string[];
    WarningTitle: string;
    WarningMessage: string;
    WarningMessageForEmailConsultation?: string;
    BadgeText?: string | null;
    RequireExactMatch?: boolean;
};

export type StopWordsMap = Record<string, StopWordDetails>;

export type StopWordMatch = {
    title: string;
    message: string;
    matchedWord: string;
    badgeText?: string | null;
};

type UseStopWordsReturn = {
    stopwordMatchSubject: StopWordMatch | null;
    stopwordMatchMessage: StopWordMatch | null;
    showSubjectWarning: boolean;
    showMessageWarning: boolean;
    checkForStopwords: (text: string, inputType: InputType) => void;
    closeSubjectWarning: () => void;
    closeMessageWarning: () => void;
    logStopWordsOnSubmit: () => void;
};

type UseStopWordsParams = {
    consultationType: ConsultationTypeEnum;
    isEmailConsultation: boolean;
};

export const useStopWords = ({
    consultationType,
    isEmailConsultation,
}: UseStopWordsParams): UseStopWordsReturn => {
    const [stopwordMatchSubject, setStopwordMatchSubject] =
        useState<StopWordMatch | null>(null);
    const [stopwordMatchMessage, setStopwordMatchMessage] =
        useState<StopWordMatch | null>(null);
    const [stopWordsShown, setStopWordsShown] = useState<string[]>([]);

    const { data: stopWords = null } = useQuery({
        queryKey: ['stopWords', consultationType],
        queryFn: () => fetchStopWords(consultationType),
        enabled: true,
        staleTime: 1000 * 60 * 10, // 10 min
        refetchOnMount: true,
    });

    const { minSingleWordLength, minCompoundWordLength } = useMemo(() => {
        let shortest = 0;
        let shortestCompound = 0;
        if (!stopWords) {
            return {
                minSingleWordLength: shortest,
                minCompoundWordLength: shortestCompound,
            };
        }

        Object.values(stopWords).forEach((details) => {
            details.Words.forEach((word) => {
                const isCompound = word.includes(' ');
                if (!isCompound) {
                    shortest =
                        shortest === 0
                            ? word.length
                            : Math.min(shortest, word.length);
                } else {
                    shortestCompound =
                        shortestCompound === 0
                            ? word.length
                            : Math.min(shortestCompound, word.length);
                }
            });
        });

        const getMaxDistance = (len: number) =>
            len < 5 ? 0 : len < 8 ? 1 : len <= 12 ? 2 : 3;

        return {
            minSingleWordLength: shortest - getMaxDistance(shortest),
            minCompoundWordLength:
                shortestCompound - getMaxDistance(shortestCompound),
        };
    }, [stopWords]);

    const isStopWordMatching = (
        stopWord: string,
        wordToMatch: string,
        maxDistance: number,
        requireExactMatch: boolean,
    ) => {
        if (requireExactMatch) return stopWord.toLowerCase() === wordToMatch;
        if (stopWord.length > wordToMatch.length + maxDistance) return false;
        if (stopWord.length < wordToMatch.length - maxDistance) return false;
        if (maxDistance === 0) return stopWord.toLowerCase() === wordToMatch;
        return (
            getLevenshteinDistance(wordToMatch, stopWord, maxDistance) <=
            maxDistance
        );
    };

    const checkForStopwords = (text: string | null, inputType: InputType) => {
        if (!text || !stopWords || !Object.keys(stopWords).length) return;

        const words = text.split(' ');
        if (!words.length) return;

        const lastWord =
            words.pop()?.replace('.', ' ').trim().toLowerCase() ?? '';
        const secondToLastWord =
            words.pop()?.replace('.', ' ').trim().toLowerCase() ?? '';
        const lastTwoWords = secondToLastWord
            ? secondToLastWord + ' ' + lastWord
            : lastWord;

        const checkSingle = lastWord.length >= minSingleWordLength;
        const checkCompound = lastTwoWords.length >= minCompoundWordLength;
        if (!checkSingle && !checkCompound) return;

        const maxSingle =
            lastWord.length < 5
                ? 0
                : lastWord.length < 8
                  ? 1
                  : lastWord.length <= 12
                    ? 2
                    : 3;
        const maxCompound =
            lastTwoWords.length < 5
                ? 0
                : lastTwoWords.length < 8
                  ? 1
                  : lastTwoWords.length <= 12
                    ? 2
                    : 3;

        let foundCompoundStopWordMatch = false;

        if (checkCompound) {
            for (const [category, details] of Object.entries(stopWords)) {
                for (const word of details.Words) {
                    if (!word.includes(' ')) continue;

                    const trimmed = word.replace(' ', '');
                    if (
                        isStopWordMatching(
                            trimmed,
                            lastTwoWords.replace(' ', ''),
                            maxCompound,
                            details.RequireExactMatch === true,
                        )
                    ) {
                        handleMatch(word, category, details, inputType);
                        foundCompoundStopWordMatch = true;
                        return;
                    }
                }
            }
        }

        if (checkSingle && !foundCompoundStopWordMatch) {
            for (const [category, details] of Object.entries(stopWords)) {
                for (const word of details.Words) {
                    if (word.includes(' ')) continue;

                    if (
                        isStopWordMatching(
                            word,
                            lastWord,
                            maxSingle,
                            details.RequireExactMatch === true,
                        )
                    ) {
                        handleMatch(word, category, details, inputType);
                        return;
                    }
                }
            }
        }
    };

    const handleMatch = (
        matchedWord: string,
        category: string,
        details: StopWordDetails,
        inputType: InputType,
    ) => {
        const message =
            isEmailConsultation && details.WarningMessageForEmailConsultation
                ? details.WarningMessageForEmailConsultation
                : details.WarningMessage;

        const match: StopWordMatch = {
            title: details.WarningTitle,
            message,
            matchedWord: matchedWord,
            badgeText: details.BadgeText,
        };

        if (inputType === InputType.Subject) {
            setStopwordMatchSubject(match);
        } else if (inputType === InputType.Message) {
            setStopwordMatchMessage(match);
        }

        setStopWordsShown((prev) => [...prev, matchedWord]);

        Log.info(
            `Caught StopWord: ${matchedWord} (in StopWord category: ${category})`,
        );

        // We don't want to show (or look for) the same warning twice
        deleteCategory(category);
    };

    const deleteCategory = (category: string) => {
        queryClient.setQueryData<StopWordsMap>(
            ['stopWords', consultationType],
            (old) => {
                if (!old) return old;
                const updated = { ...old };
                delete updated[category];
                return updated;
            },
        );
    };

    const closeSubjectWarning = () => setStopwordMatchSubject(null);
    const closeMessageWarning = () => setStopwordMatchMessage(null);

    const logStopWordsOnSubmit = () => {
        if (!stopWordsShown?.length) return;
        const logMessage = `Booking flow continued after following StopWords were shown: ${stopWordsShown.join(', ')}.`;
        Log.info(logMessage);
    };

    return {
        stopwordMatchSubject,
        stopwordMatchMessage,
        showSubjectWarning: !!stopwordMatchSubject,
        showMessageWarning: !!stopwordMatchMessage,
        checkForStopwords,
        closeSubjectWarning,
        closeMessageWarning,
        logStopWordsOnSubmit,
    };
};

const getLevenshteinDistance = (
    input: string,
    target: string,
    maxDistance: number,
) => {
    const matrix: number[][] = Array.from(
        { length: input.length + 1 },
        (_, i) =>
            Array.from({ length: target.length + 1 }, (_, j) =>
                i === 0 ? j : j === 0 ? i : 0,
            ),
    );

    for (let i = 1; i <= input.length; i++) {
        let minInRow = Infinity;
        for (let j = 1; j <= target.length; j++) {
            const cost = input[i - 1] === target[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j - 1] + cost,
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
            );
            minInRow = Math.min(minInRow, matrix[i][j]);
        }
        if (minInRow > maxDistance) return maxDistance + 1;
    }
    return matrix[input.length][target.length];
};
